/**
 * Trust strip (S6, C4): one quiet line after a preview —
 * `Read-only ✓ · 2 result sets · 243 rows (capped) · 84 ms · ok at 30s`.
 * If the chosen interval is shorter than 5× the rolling-median duration the
 * last segment turns amber (`slow for 30s — Review`). A policy-blocked
 * preview (write statement) renders the writes line — ONE warning glyph (the
 * icon), never an icon plus a repeated `⚠` character; the engine's own policy
 * message stays in the hover title rather than comma-splicing the line.
 */
import { CheckCircle2, TriangleAlert } from 'lucide-react';
import { median, REFRESH_GUARDRAIL_FACTOR } from '../../utils/durationGuardrail';
import { formatPreviewDuration } from './trustStripFormat';
import type { TilePreviewState } from './useTilePreview';

export function TrustStrip({
  preview,
  durationsMs,
  refresh,
  resultSetIndex = 0,
}: {
  preview: TilePreviewState;
  durationsMs: number[];
  refresh: { mode: 'manual' | 'interval'; seconds: number };
  /** Which result set the dialog is previewing (row count follows it). */
  resultSetIndex?: number;
}) {
  if (preview.status === 'idle' || preview.status === 'running') return null;
  if (preview.status === 'blocked') {
    return (
      <div
        className="flex items-center gap-1.5 text-xs text-warning"
        data-testid="add-tile-trust-strip"
      >
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span title={preview.message}>
          Writes — previews stay read-only. Run the tile manually to execute.
        </span>
      </div>
    );
  }
  if (preview.status === 'error') {
    return (
      <div className="text-xs text-destructive" data-testid="add-tile-trust-strip">
        {preview.message}
      </div>
    );
  }

  const { result } = preview;
  const rowCount = result.resultSets[resultSetIndex]?.rowCount ?? result.selected.rows.length;
  const medianMs = median(durationsMs);
  const tooSlow =
    refresh.mode === 'interval' && medianMs > 0 && refresh.seconds * 1000 < medianMs * REFRESH_GUARDRAIL_FACTOR;

  const segments: string[] = ['Read-only ✓'];
  if (result.resultSets.length > 1) segments.push(`${result.resultSets.length} result sets`);
  segments.push(`${rowCount} ${rowCount === 1 ? 'row' : 'rows'}${result.rowsCapped ? ' (capped)' : ''}`);
  segments.push(formatPreviewDuration(result.durationMs));

  return (
    <div
      className="flex items-center gap-1.5 text-xs text-muted-foreground"
      data-testid="add-tile-trust-strip"
    >
      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
      <span>{segments.join(' · ')}</span>
      {refresh.mode === 'interval' && (
        <span
          className={tooSlow ? 'font-medium text-warning' : ''}
          title={
            tooSlow
              ? `The preview took ~${Math.round(medianMs)} ms; refreshing every ${refresh.seconds}s leaves little headroom. Pick a longer interval or a faster query.`
              : undefined
          }
        >
          · {tooSlow ? `slow for ${refresh.seconds}s — Review` : `ok at ${refresh.seconds}s`}
        </span>
      )}
      {refresh.mode === 'manual' && <span>· manual refresh</span>}
    </div>
  );
}
